import { useState, useCallback, useEffect } from "react";
import ConvoAIPanel, { ConvoAIMessage } from "./convoai-hub";
import { CTAButton, MessageBodyPreviewProps, RightPanelProps, Template, TemplateVariables, VariablesFormProps } from "../../../pages/create-campaign/types";
import { PhoneMockup } from "./phone-mock";
import { CUSTOMER_NAME_OPTIONS, MessagingProps, TEMPLATES, typeBadgeClass, VARIABLE_COLORS } from "../../../pages/create-campaign/data";
import useRequest from "../../hook/use-req";
import { CampaignData, CampaignContent } from "../../types";



interface TemplateListItemProps {
  template: Template;
  selected: boolean;
  onSelect: (t: Template) => void;
}

function TemplateListItem({ template, selected, onSelect }: TemplateListItemProps) {
  return (
    <button
      onClick={() => onSelect(template)}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors hover:bg-slate-50
        ${selected ? "bg-indigo-50 border-l-4 border-l-indigo-500" : "border-l-4 border-l-transparent"}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-semibold text-slate-800 text-sm truncate">{template.name}</span>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${typeBadgeClass(template.type)}`}>
          {template.type}
        </span>
      </div>
      <p className="text-xs text-slate-400 truncate leading-relaxed">{template.preview}</p>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// 2. TemplateSidebar
// ─────────────────────────────────────────────────────────────

interface TemplateSidebarProps {
  templates: Template[];
  selected: Template | null;
  onSelect: (t: Template) => void;
}

function TemplateSidebar({ templates, selected, onSelect }: TemplateSidebarProps) {
  const [query, setQuery] = useState("");
  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full border-r border-slate-200">
      <div className="px-4 pt-5 pb-3">
        <h1 className="text-xl font-bold text-slate-900">Choose Your Template</h1>
        <p className="text-xs text-slate-400 mt-0.5">Pick a message template to edit</p>
      </div>
      <div className="px-4 pb-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search Templates"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full border border-slate-200 rounded-lg pl-3 pr-9 py-2 text-sm
              placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
          />
          <svg className="absolute right-3 top-2.5 text-slate-400 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-8">No templates found</p>
        ) : (
          filtered.map(t => (
            <TemplateListItem key={t.id} template={t} selected={selected?.id === t.id} onSelect={onSelect} />
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 3. MediaUpload
// ─────────────────────────────────────────────────────────────

interface MediaUploadProps {
  onUpload?: (file: File) => void;
  uploadedFile?: File | null;
}

function MediaUpload({ onUpload, uploadedFile }: MediaUploadProps) {
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && onUpload) onUpload(file);
  }, [onUpload]);

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors
        ${dragging ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-slate-50/50"}`}
    >
      {uploadedFile ? (
        <div className="flex items-center justify-center gap-2 text-emerald-600 text-sm font-medium">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {uploadedFile.name}
        </div>
      ) : (
        <>
          <p className="text-indigo-500 text-sm font-medium mb-3">Upload header image or video</p>
          <label className="inline-block cursor-pointer">
            <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.mp4"
              onChange={e => { const f = e.target.files?.[0]; if (f && onUpload) onUpload(f); }} />
            <span className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium
              text-slate-700 hover:bg-slate-100 transition-colors shadow-sm">
              Browse Files
            </span>
          </label>
          <p className="text-xs text-slate-400 mt-3">Supports JPG, PNG, MP4 &nbsp;•&nbsp; max 16MB</p>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 4. MessageBodyPreview
// ─────────────────────────────────────────────────────────────

function MessageBodyPreview({ template, vars }: MessageBodyPreviewProps) {
  const varValues = [
    vars.customerName || "{{1}}",
    vars.saleName || "{{2}}",
    vars.discount || "{{3}}",
    vars.promoCode || "{{4}}",
    vars.expiryDate || "{{5}}",
  ];
  const parts = template.preview.split(/({{[1-9]}})/g);

  return (
    <div className="mb-5">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Message Body</p>
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-700 leading-relaxed">
        {parts.map((part, i) => {
          const match = part.match(/{{([1-9])}}/);
          if (match) {
            const idx = parseInt(match[1]) - 1;
            const val = varValues[idx] || part;
            const color = VARIABLE_COLORS[idx % VARIABLE_COLORS.length];
            return (
              <span key={i} className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold mx-0.5 ${color}`}>
                {val}
              </span>
            );
          }
          return <span key={i}>{part}</span>;
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 5. VariablesForm
// ─────────────────────────────────────────────────────────────

function VariablesForm({ vars, onChange }: VariablesFormProps) {
  const set = (key: keyof TemplateVariables) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      onChange({ ...vars, [key]: e.target.value });

  const generateCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    onChange({ ...vars, promoCode: code });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold mr-1">1</span>
          Customer Name Variable
        </label>
        <div className="relative">
          <select value={vars.customerName} onChange={set("customerName")}
            className="w-full appearance-none border border-slate-200 rounded-lg px-3 py-2.5 text-sm
              bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 pr-8">
            {CUSTOMER_NAME_OPTIONS.map(o => <option key={o}>{o}</option>)}
          </select>
          <svg className="absolute right-2.5 top-3 w-4 h-4 text-slate-400 pointer-events-none"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold mr-1">2</span>
          Sale Name
        </label>
        <input value={vars.saleName} onChange={set("saleName")} placeholder="e.g. Diwali Sale"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm
            placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold mr-1">3</span>
          Discount %
        </label>
        <input value={vars.discount} onChange={set("discount")} placeholder="e.g. 15" type="number"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm
            placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold mr-1">4</span>
          Promo Code
        </label>
        <div className="flex gap-2">
          <input value={vars.promoCode} onChange={set("promoCode")} placeholder="Generate Code"
            className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm
              placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          <button onClick={generateCode} title="Generate"
            className="p-2.5 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold mr-1">5</span>
          Expiry Date
        </label>
        <input value={vars.expiryDate} onChange={set("expiryDate")} placeholder="e.g. 9 March 2026"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm
            placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 6. CTAButtonForm
// ─────────────────────────────────────────────────────────────

interface CTAButtonFormProps {
  cta: CTAButton;
  onChange: (c: CTAButton) => void;
}

function CTAButtonForm({ cta, onChange }: CTAButtonFormProps) {
  return (
    <div className="mt-6">
      <p className="text-xs font-semibold text-indigo-500 uppercase tracking-wider mb-3">CTA Button</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Button CTA</label>
          <input value={cta.label} onChange={e => onChange({ ...cta, label: e.target.value })}
            placeholder="Take survey"
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm
              placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Button URL</label>
          <input value={cta.url} onChange={e => onChange({ ...cta, url: e.target.value })}
            placeholder="url"
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm
              placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 7. RightPanel
// ─────────────────────────────────────────────────────────────

function RightPanel({
  template, vars, cta, mediaFile,
  isConvoOpen, onToggleConvo, onConvoSend,
}: RightPanelProps & { onConvoSend: (message: string, history: ConvoAIMessage[]) => Promise<string> }) {
  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-slate-100 shrink-0">
        <span className="text-sm font-semibold text-slate-700">Live Preview</span>
        <button
          onClick={onToggleConvo}
          title={isConvoOpen ? "Back to preview" : "Open ConvoAI"}
          className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors
            ${isConvoOpen
              ? "bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
              : "text-slate-500 hover:text-slate-700"}`}
        >
          {isConvoOpen ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
        </button>
      </div>

      <div className="flex-1 relative overflow-hidden">
        <div className={`absolute inset-0 flex flex-col items-center justify-start pt-6 pb-4 px-4
          overflow-y-auto transition-opacity duration-200
          ${isConvoOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          <PhoneMockup template={template} vars={vars} cta={cta} mediaFile={mediaFile} />
          <button
            onClick={onToggleConvo}
            className="mt-5 w-10 h-10 rounded-full bg-[#075E54] flex items-center justify-center shadow-lg
              hover:bg-[#054d44] transition-colors"
            title="Open ConvoAI"
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v7a2 2 0 01-2 2H9l-4 4V14H4a2 2 0 01-2-2V5z" />
            </svg>
          </button>
        </div>

        <div className={`absolute inset-0 transition-opacity duration-200
          ${isConvoOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ConvoAIPanel
            isOpen={isConvoOpen}
            onClose={onToggleConvo}
            onSend={onConvoSend}
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 8. TemplateEditor
// ─────────────────────────────────────────────────────────────

interface TemplateEditorProps {
  template: Template;
  vars: TemplateVariables;
  cta: CTAButton;
  onVarsChange: (v: TemplateVariables) => void;
  onCTAChange: (c: CTAButton) => void;
  onBack: () => void;
  onLaunch: () => void;
  onMediaUpload: (f: File) => void;
  mediaFile: File | null;
}

function TemplateEditor({
  template, vars, cta,
  onVarsChange, onCTAChange,
  onBack, onLaunch, onMediaUpload, mediaFile,
}: TemplateEditorProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-5 pb-3 border-b border-slate-100 shrink-0">
        <h2 className="text-base font-bold text-slate-900">{template.name}</h2>
        <p className="text-xs text-slate-400 mt-0.5">Fill variables to personalise your message</p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {template.type === "Media + Text" && (
          <MediaUpload onUpload={onMediaUpload} uploadedFile={mediaFile} />
        )}
        <MessageBodyPreview template={template} vars={vars} />
        <VariablesForm vars={vars} onChange={onVarsChange} />
        <CTAButtonForm cta={cta} onChange={onCTAChange} />
      </div>

      <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 shrink-0">
        <button onClick={onBack}
          className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold
            text-slate-600 hover:bg-slate-50 transition-colors">
          Back
        </button>
        <button onClick={onLaunch}
          className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white
            text-sm font-semibold shadow-sm transition-colors">
          Preview and Launch
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 9. LaunchModal
// ─────────────────────────────────────────────────────────────

interface LaunchModalProps {
  template: Template;
  vars: TemplateVariables;
  cta: CTAButton;
  mediaFile: File | null;
  onClose: () => void;
  onConfirm: () => void;
}

function LaunchModal({ template, vars, cta, mediaFile, onClose, onConfirm }: LaunchModalProps) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Preview &amp; Launch</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <div className="py-6 flex justify-center bg-slate-50">
          <PhoneMockup template={template} vars={vars} cta={cta} mediaFile={mediaFile} />
        </div>
        <div className="px-6 pb-5 pt-4 flex justify-end gap-3">
          <button onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Edit
          </button>
          <button onClick={onConfirm}
            className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm">
            🚀 Launch Campaign
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 10. Main Messaging Component
// ─────────────────────────────────────────────────────────────

export default function Messaging({
  campaignData,
  setCampaignData,
  onValidation,
  onSubmit,
  onBack,
}: MessagingProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(() => {
    if (campaignData.content?.template_id) {
      return (
        TEMPLATES.find(t => t.id === campaignData.content?.template_id) || null
      );
    }
    return TEMPLATES[0] || null;
  });

  const [isConvoOpen, setIsConvoOpen] = useState(false);
  const token = localStorage.getItem("authToken");
  const [showLaunch, setShowLaunch] = useState(false);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<"editor" | "preview">("editor");

  const { makeRequest } = useRequest("/campaign/update", "PUT", { Authorization: `Bearer ${token}` });

  // Initialize vars from campaignData or defaults
  const [vars, setVars] = useState<TemplateVariables>(() => {
    // Safely access variables with optional chaining
    const variables = campaignData.content?.variables;
    if (variables) {
      return {
        customerName: variables["1"] || "First_Name",
        saleName: variables["2"] || "",
        discount: variables["3"] || "",
        promoCode: variables["4"] || "",
        expiryDate: variables["5"] || "",
      };
    }
    return {
      customerName: "First_Name",
      saleName: "",
      discount: "",
      promoCode: "",
      expiryDate: "",
    };
  });

  // Initialize CTA from campaignData or defaults
  const [cta, setCTA] = useState<CTAButton>(() => {
    // Safely access buttons with optional chaining
    const buttons = campaignData.content?.buttons;
    if (buttons && buttons.length > 0) {
      return {
        label: buttons[0].text || "Take survey",
        url: buttons[0].url || "",
      };
    }
    return { label: "Take survey", url: "" };
  });

  // Update campaignData when vars or cta change
  // Update the useEffect that creates campaignContent - specifically the header_value
  useEffect(() => {
    if (selectedTemplate) {
      // Create variables object safely
      const variables: Record<string, string> = {};

      // Only add variables that have values
      if (vars.customerName && vars.customerName !== "First_Name") {
        variables["1"] = vars.customerName;
      } else {
        variables["1"] = "{{1}}";
      }

      if (vars.saleName) {
        variables["2"] = vars.saleName;
      } else {
        variables["2"] = "{{2}}";
      }

      if (vars.discount) {
        variables["3"] = vars.discount;
      } else {
        variables["3"] = "{{3}}";
      }

      if (vars.promoCode) {
        variables["4"] = vars.promoCode;
      } else {
        variables["4"] = "{{4}}";
      }

      if (vars.expiryDate) {
        variables["5"] = vars.expiryDate;
      } else {
        variables["5"] = "{{5}}";
      }

      // FIX: Only create object URL if mediaFile exists and is valid
      let headerValue = "";
      if (mediaFile && mediaFile instanceof File) {
        // Clean up previous object URL to prevent memory leaks
        if (campaignData.content?.header_value && campaignData.content.header_value.startsWith('blob:')) {
          URL.revokeObjectURL(campaignData.content.header_value);
        }
        headerValue = URL.createObjectURL(mediaFile);
      }

      const campaignContent: CampaignContent = {
        template_id: selectedTemplate.id,
        template_name: selectedTemplate.name,
        variables: variables,
        header_type: mediaFile ? (mediaFile.type.startsWith('image/') ? 'image' : 'video') : 'none',
        header_value: headerValue,
        message: selectedTemplate.preview,
        footer: "Reply STOP to unsubscribe",
        buttons: cta.label && cta.url ? [{
          type: "URL",
          text: cta.label,
          url: cta.url
        }] : []
      };

      setCampaignData((prev: CampaignData) => ({
        ...prev,
        content: campaignContent,
        messages: selectedTemplate.preview,
        link: cta.url
      }));

      // Validate that required fields are filled
      let isValid = true;

      // Check if template exists
      if (!selectedTemplate) {
        isValid = false;
      }

      // Check media requirement for Media + Text templates
      if (selectedTemplate.type === "Media + Text" && !mediaFile) {
        isValid = false;
      }

      // Check CTA requirement (if label exists, URL must also exist)
      if (cta.label && !cta.url) {
        isValid = false;
      }

      onValidation?.(isValid);
    }

    // Cleanup function to revoke object URLs when component unmounts or mediaFile changes
    return () => {
      if (campaignData.content?.header_value && campaignData.content.header_value.startsWith('blob:')) {
        URL.revokeObjectURL(campaignData.content.header_value);
      }
    };
  }, [selectedTemplate, vars, cta, mediaFile, setCampaignData, onValidation]);
  const handleConvoSend = async (
    message: string,
    _history: ConvoAIMessage[]
  ): Promise<string> => {
    await new Promise(r => setTimeout(r, 800));
    return "Got it! I've noted your request. Tweak the variables and hit Generate when ready.";
  };

  const handleLaunch = () => {
    if (onSubmit) {
      // Create a clean copy of the data to submit
      const submitData: Partial<CampaignData> = {
        ...(campaignData.content && { content: campaignData.content }),
        messages: campaignData.messages,
        link: campaignData.link
      };
      onSubmit(submitData);
    }
    setShowLaunch(true);
  };

  const handleConfirmLaunch = () => {
    setShowLaunch(false);
    // This will be handled by the parent component's navigation
  };

  return (
    <div className="min-h-screen font-sans">
      {/* ── Mobile top bar ── */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200 shadow-sm">
        <button onClick={() => setMobileSidebarOpen(true)}
          className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          Templates
        </button>
        {selectedTemplate && (
          <span className="text-xs font-medium text-indigo-600 truncate max-w-[160px]">
            {selectedTemplate.name}
          </span>
        )}
        <button
          onClick={() => setMobileTab(t => t === "editor" ? "preview" : "editor")}
          className="text-xs font-semibold text-indigo-600"
        >
          {mobileTab === "editor" ? "Preview" : "Edit"}
        </button>
      </div>

      {/* ── Mobile sidebar overlay ── */}
      {mobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-72 bg-white h-full shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <span className="font-bold text-slate-800">Templates</span>
              <button onClick={() => setMobileSidebarOpen(false)} className="text-slate-400 text-xl">×</button>
            </div>
            <div className="flex-1 overflow-hidden">
              <TemplateSidebar
                templates={TEMPLATES}
                selected={selectedTemplate}
                onSelect={t => { setSelectedTemplate(t); setMobileSidebarOpen(false); }}
              />
            </div>
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileSidebarOpen(false)} />
        </div>
      )}

      {/* ── Desktop: 3-col ── */}
      <div className="hidden lg:grid lg:grid-cols-[260px_1fr_300px] h-screen">
        <TemplateSidebar
          templates={TEMPLATES}
          selected={selectedTemplate}
          onSelect={setSelectedTemplate}
        />

        <div className="overflow-hidden border-r border-slate-200">
          {selectedTemplate ? (
            <TemplateEditor
              template={selectedTemplate}
              vars={vars}
              cta={cta}
              onVarsChange={setVars}
              onCTAChange={setCTA}
              onBack={onBack}
              onLaunch={handleLaunch}
              onMediaUpload={setMediaFile}
              mediaFile={mediaFile}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">
              Select a template to get started
            </div>
          )}
        </div>

        <RightPanel
          template={selectedTemplate}
          vars={vars}
          cta={cta}
          mediaFile={mediaFile}
          isConvoOpen={isConvoOpen}
          onToggleConvo={() => setIsConvoOpen(v => !v)}
          onConvoSend={handleConvoSend}
        />
      </div>

      {/* ── Mobile: tab-based ── */}
      <div className="lg:hidden">
        {selectedTemplate ? (
          mobileTab === "editor" ? (
            <div className="bg-white">
              <TemplateEditor
                template={selectedTemplate}
                vars={vars}
                cta={cta}
                onVarsChange={setVars}
                onCTAChange={setCTA}
                onBack={() => setSelectedTemplate(null)}
                onLaunch={handleLaunch}
                onMediaUpload={setMediaFile}
                mediaFile={mediaFile}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 bg-slate-50 min-h-screen gap-6">
              <PhoneMockup template={selectedTemplate} vars={vars} cta={cta} mediaFile={mediaFile} />
              <button
                onClick={() => setIsConvoOpen(true)}
                className="w-10 h-10 rounded-full bg-[#075E54] flex items-center justify-center shadow-lg"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v7a2 2 0 01-2 2H9l-4 4V14H4a2 2 0 01-2-2V5z" />
                </svg>
              </button>
              {isConvoOpen && (
                <div className="fixed inset-0 z-50 bg-white">
                  <ConvoAIPanel
                    isOpen={isConvoOpen}
                    onClose={() => setIsConvoOpen(false)}
                    onSend={handleConvoSend}
                  />
                </div>
              )}
            </div>
          )
        ) : (
          <div className="p-4">
            <TemplateSidebar templates={TEMPLATES} selected={null} onSelect={setSelectedTemplate} />
          </div>
        )}
      </div>

      {/* Launch Modal */}
      {showLaunch && selectedTemplate && (
        <LaunchModal
          template={selectedTemplate}
          vars={vars}
          cta={cta}
          mediaFile={mediaFile}
          onClose={() => setShowLaunch(false)}
          onConfirm={handleConfirmLaunch}
        />
      )}
    </div>
  );
}